"use client";

import { useCallback, useRef, useState } from "react";

import type { SurveyElement } from "~/types/Survey";
import { LIVE_FEEDBACK_MESSAGE_TEMPLATE } from "../liveFeedback";

type Choice = {
    text?: string;
    value?: unknown;
};

const ANSWER_PLACEHOLDER = "{{answer}}";

const isChoice = (choice: unknown): choice is Choice => {
    return typeof choice === "object" && choice !== null;
};

const getAnswerText = (question: SurveyElement, value: unknown) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }

    const matchingChoice = question.choices?.find((choice) => {
        return isChoice(choice) && choice.value === value;
    });

    if (isChoice(matchingChoice) && typeof matchingChoice.text === "string") {
        return matchingChoice.text;
    }

    return String(value);
};

export const useLiveFeedback = () => {
    const [message, setMessage] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const hasShownRef = useRef(false);

    const show = useCallback((
        question: SurveyElement,
        value: unknown,
        isUserInitiated = true
    ) => {
        if (!isUserInitiated || hasShownRef.current) return;

        const answerText = getAnswerText(question, value);
        if (!answerText) return;

        hasShownRef.current = true;
        setMessage(LIVE_FEEDBACK_MESSAGE_TEMPLATE.replaceAll(
            ANSWER_PLACEHOLDER,
            answerText
        ));
        setIsOpen(true);
    }, []);

    const dismiss = useCallback(() => {
        setIsOpen(false);
    }, []);

    return {
        dismiss,
        isOpen,
        message,
        show,
    };
};
